<template>
  <main role="main">
    <b-container class="py-4">
      <div class="d-flex flex-row align-items-center justify-content-between mb-4">
        <h4 class="m-0">Danh sách địa điểm họp</h4>
        <div>
          <b-button class="mr-4" variant="outline-secondary" @click="debouncedOnClickReloadBtn">
            <b-icon icon="arrow-clockwise"></b-icon> {{ $t("reload") }}
          </b-button>
          <b-button variant="outline-primary" v-b-modal:new-location-modal v-if="isCustomer()">
            <b-icon icon="plus"></b-icon> {{ $t("createNew") }}
          </b-button>
        </div>
      </div>

      <div class="d-flex flex-row align-items-center justify-content-between mb-3">
        <div>
          <b-form class="d-inline-block" inline @submit.prevent="debouncedOnClickSearch">
            <b-form-input class="mr-2" style="width: 300px;" type="search"
                          minlength="2" maxlength="200" v-model="searchText" @keyup.enter="debouncedOnClickSearch"/>
            <b-button type="submit" variant="primary">{{ $t("search") }}</b-button>
          </b-form>
        </div>
        <div class="d-flex flex-row align-items-center">
          <div v-if="totalItems" class="text-right flex-shrink-0 mr-3">{{ $t("resultCount", { count: totalItems }) }}</div>
          <b-pagination class="my-0 mr-3" v-model="currentPage" :total-rows="totalItems" :per-page="itemsPerPage"></b-pagination>
          <b-form-select v-model="itemsPerPage" :options="itemsPerPageSelections"></b-form-select>
        </div>
      </div>

      <b-table ref="table" :fields="tableFields" :items="tableItemProvider" hover primary-key="id" show-empty
               :per-page="itemsPerPage" :current-page="currentPage">
        <template v-slot:table-busy>
          <div class="text-center text-secondary py-4">{{ $t("loadingData") }}</div>
        </template>
        <template v-slot:empty>
          <div class="text-center text-secondary py-4">{{ $t("noData") }}</div>
        </template>
        <template v-slot:cell(actions)="data">
          <b-button variant="outline-primary" size="sm" class="mr-3" @click="onClickEditItem(data.item)">Sửa</b-button>
          <b-button variant="outline-danger" size="sm" @click="onClickDeleteItem(data.item)">{{ $t("actionDelete") }}</b-button>
        </template>
      </b-table>
    </b-container>

    <b-modal id="new-location-modal" title="Thêm địa điểm mới" hide-footer no-close-on-backdrop>
      <NewLocationModal modal-id="new-location-modal" @submitted="refreshData" />
    </b-modal>

    <b-modal id="edit-location-modal" title="Cập nhật địa điểm" hide-footer no-close-on-backdrop>
      <EditLocationModal modal-id="edit-location-modal" :item="editingItem" @submitted="refreshData" />
    </b-modal>

  </main>
</template>

<style lang="scss" scoped>

</style>

<script lang="ts">
  import {Component, Ref, Vue} from 'vue-property-decorator';
  import _ from "lodash";
  import BaseComponent from "@/components/BaseComponent";
  import {BvTableFieldArray} from "bootstrap-vue";
  import {BTable, BvTableCtxObject, BvTableProviderPromiseResult} from "bootstrap-vue/src/components/table";
  import * as calendarService from "@/service/calendarService";
  import NewLocationModal from "@/components/calendar/NewLocationModal.vue";
  import EditLocationModal from "@/components/calendar/EditLocationModal.vue";
  import { BounceTimes } from "@/views/enums/TimesNumbers";

  @Component({
    components: {EditLocationModal, NewLocationModal }
  })
  export default class LocationList extends BaseComponent {

    tableFields: BvTableFieldArray = [
      {
        key: "id",
        label: "#",
        thClass: "text-nowrap",
      },
      {
        key: "name",
        label: "Tên địa điểm",
        thClass: "text-nowrap",
      },
      {
        key: "actions",
        label: "",
        thClass: "text-nowrap",
        tdClass: "text-nowrap"
      }
    ];

    @Ref("table") table: BTable;

    searchText = "";

    items = [];
    currentPage = 1;
    totalItems = 0;
    itemsPerPage = 20;
    itemsPerPageSelections = [
      { value: 20, text: "20 " + this.$t("itemsPerPage").toString() },
      { value: 50, text: "50 " + this.$t("itemsPerPage").toString() },
      { value: 100, text: "100 " + this.$t("itemsPerPage").toString() },
      { value: 200, text: "200 " + this.$t("itemsPerPage").toString() },
    ];

    editingItem = null;

    mounted() {

    }

    debouncedOnClickReloadBtn = _.debounce(() => {
      this.onClickReloadBtn();
    }, BounceTimes.BOUNCETIMES);
    onClickReloadBtn() {
      this.searchText = "";
      this.currentPage = 1;
      this.table.refresh();
    }

    debouncedOnClickSearch = _.debounce(() => {
      this.onClickSearch();
    }, BounceTimes.BOUNCETIMES);
    onClickSearch() {
      this.table.refresh();
    }

    refreshData() {
      this.table.refresh();
    }

    async tableItemProvider(ctx: BvTableCtxObject): Promise<BvTableProviderPromiseResult> {
      try {
        const res = await calendarService.getLocations({
          q: this.searchText.trim(),
          page: this.currentPage,
          pageSize: this.itemsPerPage
        });
        this.totalItems = res.total;
        return res.data;

      } catch (e) {
        this.toastError(e);
        return [];
      }
    }

    onClickEditItem(item) {
      this.editingItem = item;
      this.$bvModal.show("edit-location-modal");
    }

    async onClickDeleteItem(item) {
      const modalMessage = `Bạn có chắc chắn muốn xoá "${item.name}" (ID: ${item.id})?`;
      const confirmDelete = await this.$bvModal.msgBoxConfirm(modalMessage, {
        okVariant: 'danger',
        okTitle: this.$t("yes").toString(),
        cancelTitle: this.$t("no").toString(),
      });

      if (confirmDelete) {
        try {
          await calendarService.deleteLocation(item.id);
          this.refreshData();
          this.toastSuccessDelay("Xoá địa điểm thành công");

        } catch (e) {
          this.toastError(e);
        }
      }
    }

  }
</script>
